import React, { useCallback, useEffect, useState } from 'react'
import { isEmpty } from 'lodash'

import ActivityModalContext from 'contexts/ActivityModalContext'

import useKanbanColumnStates from 'hooks/useKanbanColumnStates'
import useKanbanList from 'hooks/useKanbanList'
import usePermissions from 'hooks/usePermissions'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'

const ActivityModalProvider = ({ children }) => {
  const snackbar = useSnackbar()
  const permissions = usePermissions()

  const canManage = permissions.permitted(
    constants.permissions.KANBAN_TASKS.MANAGE,
  )

  const [isSaving, setIsSaving] = useState(false)
  const [departments, setDepartments] = useState([])
  const [activityToEdit, setActivityToEdit] = useState(null)
  const { addTaskToList, updateTaskInList } = useKanbanList()
  const [activityModalKey, setActivityModalKey] = useState(0)
  const [activityModalOpen, setActivityModalOpen] = useState(false)
  const { addTaskToColumn, updateTaskInColumn } = useKanbanColumnStates()

  useEffect(() => {
    const load = async () => {
      if (!activityModalOpen || !canManage || !isEmpty(departments)) return
      try {
        const res = await service.dponet.departments.get({ perPage: 9999 })
        setDepartments(res?.data?.departments || [])
      } catch {
        // silently ignore
      }
    }
    load()
    //eslint-disable-next-line
  }, [activityModalOpen, canManage])

  const handleOpenCreate = useCallback((defaultColumnId) => {
    setActivityToEdit(
      defaultColumnId ? { kanbanColumnId: defaultColumnId } : null,
    )
    setActivityModalKey((k) => k + 1)
    setActivityModalOpen(true)
  }, [])

  const handleOpenEdit = useCallback((task) => {
    setActivityToEdit(task)
    setActivityModalKey((k) => k + 1)
    setActivityModalOpen(true)
  }, [])

  const handleCloseActivityModal = useCallback((value) => {
    setActivityModalOpen(value)
  }, [])

  const handleSaveActivity = useCallback(
    async (data, taskId) => {
      setIsSaving(true)
      try {
        if (taskId) {
          let res
          res = await service.dponet.kanbanTasks.update({ taskId, ...data })
          if (data.isFinalize) {
            res = await service.dponet.kanbanTasks.finalize({ taskId })
          } else if (data.isMoveColumn) {
            res = await service.dponet.kanbanTasks.move({
              taskId,
              kanbanColumnId: data.kanbanColumnId,
            })
          }
          const updatedTask = res?.data?.task
          if (updatedTask) {
            updateTaskInColumn(updatedTask)
            updateTaskInList(updatedTask)
          }
        } else {
          const res = await service.dponet.kanbanTasks.create(data)
          const createdTask = res?.data?.task
          if (createdTask) {
            addTaskToColumn(createdTask)
            addTaskToList(createdTask)
          }
          if (data.attachments && createdTask?.id) {
            await service.dponet.kanbanTasks.uploadAttachments({
              taskId: createdTask.id,
              attachments: data.attachments,
            })
          }
        }
        if (taskId && data.attachments) {
          await service.dponet.kanbanTasks.uploadAttachments({
            taskId,
            attachments: data.attachments,
          })
        }
        snackbar.open({
          message: 'Atividade salva com sucesso',
          variant: 'success',
        })
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(error),
          variant: 'error',
        })
        throw error
      } finally {
        setIsSaving(false)
      }
    },
    [
      snackbar,
      addTaskToList,
      updateTaskInList,
      addTaskToColumn,
      updateTaskInColumn,
    ],
  )

  const handleRemoveAttachment = useCallback(
    async (taskId, attachmentId) => {
      try {
        await service.dponet.kanbanTasks.destroyAttachment({
          taskId,
          attachmentId,
        })
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(error),
          variant: 'error',
        })
      }
    },
    [snackbar],
  )

  return (
    <ActivityModalContext.Provider
      value={{
        activityToEdit,
        activityModalKey,
        activityModalOpen,
        isSaving,
        departments,
        handleOpenCreate,
        handleOpenEdit,
        handleCloseActivityModal,
        handleSaveActivity,
        handleRemoveAttachment,
      }}
    >
      {children}
    </ActivityModalContext.Provider>
  )
}

export default ActivityModalProvider
